import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Filter, ChevronUp, ChevronDown, ChevronRight,
  Eye, Edit3, Trash2
} from 'lucide-react';
import {
  EavAction, EavColumns, EavView
} from './EavListToolkit';
import Pagination from './Pagination';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const MIN_COL_WIDTH = 50;
const MAX_COL_WIDTH = 600;
const CHAR_WIDTH = 9;
const HEADER_PAD = 40;

/**
 * Reusable EAV list component.
 *
 * Props:
 *   entityType             - 'products' | 'suppliers' | 'employees'
 *   attributeSetId         - attribute set ID for form-config API
 *   flatColumns            - array of {code, label, inputType}
 *   defaultVisible         - default visible column codes
 *   titleKey               - translation key for page title
 *   searchKey              - translation key for search placeholder
 *   pageTotalKey           - translation key for "total X items"
 *   confirmDeleteKey       - translation key for batch delete confirm
 *   confirmDeleteSingleKey - translation key for single delete confirm
 *   actionsLabelKey        - translation key for actions column (default: 'products.actions')
 *   apiPrefix              - base API path, defaults to `/api/${entityType}`
 *   deleteEndpoint         - function id => URL, default `${apiPrefix}/delete/{id}`
 *   routes                 - { add, view(id), edit(id) } navigation routes
 *   renderActions          - (item) => JSX, custom actions column
 *   renderCell             - (item, col) => JSX | string, custom cell renderer
 *   treeField              - optional, field name for parent-child hierarchy
 *   usePageSizeChange      - boolean, use <Pagination> component (default: false)
 *   onBeforeDelete         - (id) => boolean, return false to cancel
 *   onBeforeBatchDelete    - (ids) => boolean, return false to cancel
 *   onBatchAssign          - (ids) => void, triggered when "批量分配负责人" is clicked
 *   onBatchRemoveResponsible - (ids) => void, triggered when "批量移除负责人" is clicked
 *   onBatchUpdateCustom    - (ids) => void, if provided, called with selected ids instead of the default prompt-based batch update
 *   afterLoad              - (items) => void, called after data loads
 *   extraToolbarButtons    - JSX, extra buttons in toolbar area
 *   filterBarExtra         - JSX, extra elements rendered after the search bar (e.g. store dropdown)
 *   externalAttributeConfig - array|null, external EAV attribute config (overrides form-config loading)
 */

/** 产品图片鼠标悬停预览 */
const ImageWithHoverPreview = ({ src }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const thumbnailRef = useRef(null);

  const handleMouseEnter = useCallback(() => setShow(true), []);
  const handleMouseLeave = useCallback(() => setShow(false), []);

  const handleMouseMove = useCallback((e) => {
    // 固定预览在缩略图右侧区域，避免跟随鼠标闪烁
    if (thumbnailRef.current) {
      const rect = thumbnailRef.current.getBoundingClientRect();
      setPos({ x: rect.right + 12, y: rect.top - 40 });
    }
  }, []);

  return (
    <>
      <div
        ref={thumbnailRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <img
          src={src}
          alt=""
          className="w-10 h-10 object-cover rounded border border-slate-200 dark:border-slate-600 cursor-pointer"
        />
      </div>
      {show && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: pos.x, top: pos.y }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <img
              src={src}
              alt=""
              className="max-w-[320px] max-h-[320px] object-contain"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const EavList = ({
  entityType,
  attributeSetId,
  flatColumns,
  defaultVisible,
  titleKey,
  searchKey,
  pageTotalKey,
  confirmDeleteKey,
  confirmDeleteSingleKey,
  actionsLabelKey = 'products.actions',
  apiPrefix: apiPrefixProp,
  deleteEndpoint,
  routes,
  renderActions,
  renderCell,
  treeField,
  treeNodeIdField = 'entity_id',
  usePageSizeChange = false,
  onBeforeDelete,
  onBeforeBatchDelete,
  onBatchAssign,
  onBatchRemoveResponsible,
  onBatchUpdateCustom,
  onBatchQcUpdate,
  onBatchPushToChannel,
  onBatchRecalculateFormulas,
  onBatchEditStatus,
  onBatchGenerateUrlKey,
  onBatchUpdateSyncStatus,
  onBatchUpdatePushStatus,
  onBatchUpdateOnline,
  onBatchUpdatePrice,
  onBatchUpdateImages,
  onBatchAssignPosition,
  afterLoad,
  extraToolbarButtons,
  headerActionsExtra,
  filterBarExtra,
  externalAttributeConfig,
  excludeFilterColumns = [],
  filterCustomRenderers = {},
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canView = true,
}) => {
  const { t, lang } = useTranslation();
  const currentLangRef = useRef(lang);
  currentLangRef.current = lang;
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const apiPrefix = apiPrefixProp || `/api/${entityType}`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') || '20', 10));
  const [total, setTotal] = useState(0);
  const [productsTotal, setProductsTotal] = useState(0);
  const [skusTotal, setSkusTotal] = useState(0);

  // EAV attribute config from API
  const [attributeConfig, setAttributeConfig] = useState([]);
  const [filterOptions, setFilterOptions] = useState({}); // attrCode => [{value, label}]

  const allColumns = useMemo(() => {
    const eavCols = attributeConfig.map(a => ({
      code: a.attribute_code,
      label: a.translations
        ? (a.translations[lang] || a.default_label || a.attribute_code)
        : (a.default_label || a.attribute_code),
      inputType: a.input_type,
      isEav: true,
      isLength: a.is_length === '1' || a.is_length === 1,
      isWeight: a.is_weight === '1' || a.is_weight === 1,
      currencyUnit: a.currency_unit,
      defaultCurrency: a.default_currency,
      weightUnit: a.weight_unit,
      lengthUnit: a.length_unit,
    }));
    return [...flatColumns, ...eavCols];
  }, [attributeConfig, flatColumns, lang]);

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [colWidths, setColWidths] = useState({});
  const [filters, setFilters] = useState({});
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [currentView, setCurrentView] = useState(t('products.default_view'));
  const [sortField, setSortField] = useState('entity_id');
  const [sortOrder, setSortOrder] = useState('DESC');

  const resizeRef = useRef(null);
  const dragRef = useRef(null);
  const userResizedRef = useRef(new Set());
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [expandedParentIds, setExpandedParentIds] = useState(new Set());
  const [unitMap, setUnitMap] = useState({}); // attrCode => unit suffix
  const [searchTerm, setSearchTerm] = useState('');
  const eavInitializedRef = useRef(false);

  // Auto-add EAV columns from defaultVisible once attribute config finishes loading
  useEffect(() => {
    if (attributeConfig.length > 0 && allColumns.length > flatColumns.length && !eavInitializedRef.current) {
      eavInitializedRef.current = true;
      setVisibleColumns(prev => {
        const missing = defaultVisible.filter(
          code => !prev.includes(code) && allColumns.some(c => c.code === code)
        );
        if (missing.length === 0) return prev;
        return missing.reduce((acc, code) => {
          const idx = defaultVisible.indexOf(code);
          const insertBefore = defaultVisible
            .slice(idx + 1)
            .find(c => acc.includes(c));
          if (insertBefore) {
            const pos = acc.indexOf(insertBefore);
            return [...acc.slice(0, pos), code, ...acc.slice(pos)];
          }
          return [...acc, code];
        }, [...prev]);
      });
    }
  }, [attributeConfig, allColumns, flatColumns, defaultVisible]);

  // 当 defaultVisible 加载完成后，同步到 visibleColumns
  useEffect(() => {
    if (defaultVisible.length > 0) {
      setVisibleColumns(prev => {
        const missing = defaultVisible.filter(c => !prev.includes(c));
        if (missing.length === 0) return prev;
        return [...prev, ...missing];
      });
    }
  }, [defaultVisible]);

  // Load saved views from localStorage (format: [{name, columns}])
  const [savedViews, setSavedViews] = useState(() => {
    try {
      const stored = localStorage.getItem(`eav_views_${entityType}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
          return parsed.map(name => ({ name, columns: null }));
        }
        return parsed;
      }
      return [{ name: t('products.default_view'), columns: null }];
    } catch { return [{ name: t('products.default_view'), columns: null }]; }
  });

  // ================================================================
  // FETCH: EAV attribute config + system params
  // ================================================================
  useEffect(() => {
    // When externalAttributeConfig is provided, use it directly
    if (externalAttributeConfig !== undefined && externalAttributeConfig !== null) {
      eavInitializedRef.current = false;
      setAttributeConfig(externalAttributeConfig);
      // Load filter options for option-based attributes
      const optionAttrs = externalAttributeConfig.filter(a =>
        ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(a.input_type)
      );
      if (optionAttrs.length > 0) {
        Promise.all(optionAttrs.map(attr =>
          fetch(`/api/settings/attributes/${attr.attribute_id}/options`)
            .then(r => r.json())
            .then(r => {
              if (r.status === 'success' && r.data) {
                return { code: attr.attribute_code, options: r.data };
              }
              return null;
            })
            .catch(() => null)
        )).then(results => {
          const opts = {};
          results.forEach(r => {
            if (r && r.options) {
              opts[r.code] = r.options.map(o => ({
                value: o.value || o.option_id,
                label: lang === 'en' && o.english_label ? o.english_label :
                        lang === 'zh' && o.chinese_label ? o.chinese_label :
                        o.label || o.default_label || o.value || ''
              }));
            }
          });
          setFilterOptions(opts);
        });
      }
      return;
    }
    // Load system params for weight/length units
    fetch('/api/settings/params')
      .then(r => r.json())
      .then(r => {
        if (r.status === 'success' && r.data) {
          const params = r.data;
          const weightUnit = params.weight_unit || 'kgs';
          const lengthUnit = params.length_unit || 'cm';
          setUnitMap({ weight: weightUnit, length: lengthUnit });
        }
      })
      .catch(() => {});
    fetch(`${apiPrefix}/form-config?attribute_set_id=${attributeSetId}`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success' && Array.isArray(res.data)) {
          setAttributeConfig(res.data);
          // Load filter options for option-based attributes
          const optionAttrs = res.data.filter(a =>
            ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(a.input_type)
          );
          if (optionAttrs.length > 0) {
            Promise.all(optionAttrs.map(attr =>
              fetch(`/api/settings/attributes/${attr.attribute_id}/options`)
                .then(r => r.json())
                .then(r => {
                  if (r.status === 'success' && r.data) {
                    return { code: attr.attribute_code, options: r.data };
                  }
                  return null;
                })
                .catch(() => null)
            )).then(results => {
              const opts = {};
              results.forEach(r => {
                if (r && r.options) {
                  opts[r.code] = r.options.map(o => ({
                    value: o.value || o.option_id,
                    label: lang === 'en' && o.english_label ? o.english_label :
                            lang === 'zh' && o.chinese_label ? o.chinese_label :
                            o.label || o.default_label || o.value || ''
                  }));
                }
              });
              setFilterOptions(opts);
            });
          }
        }
      })
      .catch(err => console.error(`Failed to load ${entityType} attribute config`, err));
  }, [externalAttributeConfig]);

  // ================================================================
  // FETCH: Data — runs on mount AND when pagination state changes
  // ================================================================
  const loadItems = useCallback(async (pageNum = 1, size = pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', pageNum);
      params.set('limit', size);
      params.set('lang', currentLangRef.current);
      if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
      if (sortField) params.set('sort_field', sortField);
      if (sortOrder) params.set('sort_order', sortOrder);
      Object.entries(filters).forEach(([key, val]) => {
        if (val && val.trim() !== '') params.set(key, val.trim());
      });
      const res = await fetch(`${apiPrefix}?${params.toString()}`);
      const data = await res.json();
      if (data.status === 'success') {
        setItems(data.data || []);
        setTotal(data.meta?.total || 0);
        setProductsTotal(data.meta?.products_total || data.meta?.total || 0);
        setSkusTotal(data.meta?.skus_total || 0);
        setPage(pageNum);
        if (afterLoad) afterLoad(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix, filters, pageSize, afterLoad, searchTerm, sortField, sortOrder]);

  // Mount & pagination-autoload
  // Mount: load page from URL params (if any)
  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const initialSize = parseInt(searchParams.get('pageSize') || '20', 10);
    loadItems(initialPage, initialSize);
  }, []);

  // Sync page/pageSize to URL search params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const sizeStr = String(pageSize);
    if (newParams.get('page') !== pageStr || newParams.get('pageSize') !== sizeStr) {
      if (page !== 1) newParams.set('page', pageStr); else newParams.delete('page');
      if (pageSize !== 20) newParams.set('pageSize', sizeStr); else newParams.delete('pageSize');
      setSearchParams(newParams, { replace: true });
    }
  }, [page, pageSize]);

  // Pagination component: auto-load when page/pageSize changes
  useEffect(() => {
    if (usePageSizeChange) {
      loadItems(page, pageSize);
    }
  }, [page, pageSize, usePageSizeChange]);

  // Auto-reload when sort field/order changes (reset to page 1)
  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (!initialLoadRef.current) {
      setPage(1);
      loadItems(1, pageSize);
    }
    initialLoadRef.current = false;
  }, [sortField, sortOrder]);

  // ================================================================
  // AUTO-WIDTH — recalculates when data OR attribute config arrives
  // ================================================================
  useEffect(() => {
    if (items.length === 0 || allColumns.length === 0) return;
    const widths = {};
    visibleColumns.forEach(code => {
      if (code === 'image') { widths[code] = 60; return; }
      const col = allColumns.find(c => c.code === code);
      if (!col) return;
      let maxLen = (col.label || code).length;
      items.forEach(item => {
        const val = getCellValue(item, col);
        if (val != null && val !== '') {
          maxLen = Math.max(maxLen, String(val).length);
        }
      });
      const w = maxLen * CHAR_WIDTH + HEADER_PAD;
      widths[code] = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, w));
    });
    setColWidths(prev => {
      const merged = { ...widths };
      userResizedRef.current.forEach(code => {
        if (code in prev) merged[code] = prev[code];
      });
      return merged;
    });
  }, [items, allColumns]); // both deps = recalc when EAV columns arrive

  // ================================================================
  // HELPERS
  // ================================================================
  const flatCodes = useMemo(() => new Set(flatColumns.map(c => c.code)), [flatColumns]);

  const getCellValue = (item, col) => {
    if (col.code === 'image') return item.image || null;
    if (col.code === 'category_id') return item.category_name || item[col.code];
    if (col.isEav) return item.attributes?.[col.code];
    return item[col.code];
  };

  const formatDate = (dateString, inputType) => {
    if (!dateString) return '-';
    try {
      // Values are already in system local timezone from backend, display as-is
      if (inputType === 'Date and Time') {
        return dateString.substring(0, 19).replace('T', ' ');
      }
      return dateString.substring(0, 10);
    } catch (err) { return dateString; }
  };

  // ================================================================
  // COLUMN RESIZE
  // ================================================================
  const handleResizeStart = useCallback((code, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { code, startX: e.clientX, startWidth: colWidths[code] || 100 };

    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const diff = ev.clientX - resizeRef.current.startX;
      const nw = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, resizeRef.current.startWidth + diff));
      setColWidths(prev => ({ ...prev, [resizeRef.current.code]: nw }));
    };
    const onUp = () => {
      if (resizeRef.current) userResizedRef.current.add(resizeRef.current.code);
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths]);

  // ================================================================
  // COLUMN REORDER
  // ================================================================
  const handleHeaderDragStart = (e, code, idx) => {
    dragRef.current = { draggedCode: code, sourceIndex: idx };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', code);
  };
  const handleHeaderDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleHeaderDrop = (targetIdx) => {
    if (!dragRef.current) return;
    const { draggedCode } = dragRef.current;
    if (!draggedCode) return;
    const fromIdx = visibleColumns.indexOf(draggedCode);
    if (fromIdx === -1 || fromIdx === targetIdx) { dragAfterDrop(); return; }
    setVisibleColumns(prev => {
      const arr = [...prev];
      arr.splice(fromIdx, 1);
      arr.splice(targetIdx, 0, draggedCode);
      return arr;
    });
    dragAfterDrop();
  };
  const dragAfterDrop = () => {
    dragRef.current = null;
    setDragOverIdx(null);
  };

  // ================================================================
  // SELECTION / BATCH
  // ================================================================
  const displayItems = treeField
    ? items.filter(item => item[treeField] === null)
    : items;

  // Resolve the tree node ID from entity_id (needed when treeField stores cp_id, not entity_id)
  const getNodeIdByEntityId = (entityId) => {
    if (treeNodeIdField === 'entity_id') return entityId;
    const item = items.find(i => i.entity_id === entityId);
    return item ? item[treeNodeIdField] : entityId;
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all parents + their children (selection operates on DB entity IDs)
      if (treeField) {
        const parentNodeIds = displayItems.map(item => item[treeNodeIdField]);
        const parentEntityIds = displayItems.map(item => item.entity_id);
        const childIds = items
          .filter(item => item[treeField] !== null && parentNodeIds.includes(item[treeField]))
          .map(item => item.entity_id);
        setSelectedIds([...parentEntityIds, ...childIds]);
      } else {
        setSelectedIds(items.map(item => item.entity_id));
      }
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const nodeId = getNodeIdByEntityId(id);
      if (prev.includes(id)) {
        // Deselect: remove this item and its children
        if (treeField) {
          const childIds = items
            .filter(item => item[treeField] === nodeId)
            .map(item => item.entity_id);
          return prev.filter(i => i !== id && !childIds.includes(i));
        }
        return prev.filter(i => i !== id);
      } else {
        // Select: add this item and its children
        if (treeField) {
          const childIds = items
            .filter(item => item[treeField] === nodeId)
            .map(item => item.entity_id);
          return [...new Set([...prev, id, ...childIds])];
        }
        return [...prev, id];
      }
    });
  };

  const handleDelete = async (id) => {
    if (onBeforeDelete && !onBeforeDelete(id)) return;
    if (!window.confirm(t(confirmDeleteSingleKey || `${entityType}.confirm_delete_single`))) return;
    try {
      const url = deleteEndpoint ? deleteEndpoint(id) : `${apiPrefix}/delete/${id}`;
      const res = await fetch(url, { method: 'DELETE' });
      const data = res.headers?.get('content-type')?.includes('application/json')
        ? await res.json() : null;
      if (data?.status === 'success') { loadItems(page, pageSize); notify(t('common.success'), 'success'); }
      else { notify(data?.message || t('common.error'), 'error'); }
    } catch (err) { notify(t('common.error'), 'error'); }
  };

  const handleBatchDelete = async (ids) => {
    if (onBeforeBatchDelete && !onBeforeBatchDelete(ids)) return;
    if (!window.confirm(t(confirmDeleteKey || `${entityType}.confirm_delete`).replace('{{count}}', ids.length))) return;
    try {
      const res = await fetch(`${apiPrefix}/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const data = res.headers?.get('content-type')?.includes('application/json')
        ? await res.json() : null;
      if (data?.status === 'success') { setSelectedIds([]); loadItems(page, pageSize); }
      else { notify(data?.message || t('common.error'), 'error'); }
    } catch (err) { notify(t('common.error'), 'error'); }
  };

  const handleBatchUpdate = async (ids) => {
    if (onBatchUpdateCustom) {
      onBatchUpdateCustom(ids);
      return;
    }
    const attribute = prompt(t('employees.batch_update_prompt_code'));
    if (!attribute) return;
    const value = prompt(t('employees.batch_update_prompt_value', { attribute }));
    if (!value) return;
    try {
      const res = await fetch(`${apiPrefix}/batch-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, attribute, value }),
      });
      if (res.ok) { loadItems(page, pageSize); notify(t('common.success'), 'success'); }
      else { const d = await res.json(); notify(d.message || t('common.error'), 'error'); }
    } catch (err) { notify(t('common.error'), 'error'); }
  };

  // ================================================================
  // FILTER RENDERING
  // ================================================================
  const renderFilterInput = (col) => {
    const val = filters[col.code] || '';
    const onChange = (v) => setFilters({ ...filters, [col.code]: v });
    const options = filterOptions[col.code];
    switch (col.inputType) {
      case 'Dropdown':
      case 'Multiple Select':
      case 'Visual Swatch':
      case 'Text Swatch':
        return (
          <select value={val} onChange={e => onChange(e.target.value)}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white">
            <option value="">{t('common.all')}</option>
            {options && options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case 'Date':
      case 'Date and Time':
        return (
          <input type="date" value={val} onChange={e => onChange(e.target.value)}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white" />
        );
      case 'Yes/No':
        return (
          <select value={val} onChange={e => onChange(e.target.value)}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white">
            <option value="">{t('common.all')}</option>
            <option value="1">{t('common.yes')}</option>
            <option value="0">{t('common.no')}</option>
          </select>
        );
      default:
        return (
          <input type="text" value={val} onChange={e => onChange(e.target.value)}
            placeholder={t('common.filter_placeholder', { field: col.label })}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white" />
        );
    }
  };

  // ================================================================
  // DEFAULT CELL & ACTIONS RENDERERS
  // ================================================================
  const defaultRenderCell = (item, col) => {
    const val = getCellValue(item, col);
    if (col.code === 'image') {
      return val
        ? <ImageWithHoverPreview src={val} />
        : <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center text-slate-400 text-xs">-</div>;
    }
    if (col.code === 'status') {
      return val === 1 || val === '1'
        ? <span className="text-green-500">● {t('employees.active')}</span>
        : <span className="text-orange-500">● {t('employees.frozen')}</span>;
    }
    if (col.code === 'is_super_admin') {
      return val === 1 || val === '1'
        ? <span className="text-green-600 dark:text-green-400 font-semibold">{t('common.yes')}</span>
        : <span className="text-slate-400">{t('common.no')}</span>;
    }
    if (col.code === 'password') return '******';
    if (col.code === 'category_id') return val || '-';
    if (col.code === 'created_by') {
      if (item.created_by_name) {
        return (
          <span className="text-slate-700 dark:text-slate-300">
            {item.created_by} - {item.created_by_name.replace(/\s/g, '')}
          </span>
        );
      }
      return val || '-';
    }
    if (col.code === 'supplier_id') {
      if (item.supplier_name) {
        return (
          <span className="text-slate-700 dark:text-slate-300" title={`ID: ${val}`}>
            {item.supplier_name}
          </span>
        );
      }
      return val || '-';
    }
    // Resolve option value to label for option-type attributes
    if (val != null && val !== '' && col.isEav && filterOptions[col.code]) {
      const matched = filterOptions[col.code].find(o => String(o.value) === String(val));
      if (matched) return matched.label;
    }
    if (col.inputType === 'Date' || col.inputType === 'Date and Time') return formatDate(val, col.inputType);
    // Append unit suffix for currency/weight/length fields
    if (val != null && val !== '' && col.isEav) {
      let suffix = '';
      if (col.inputType === 'Price' && (col.currencyUnit || col.defaultCurrency)) {
        suffix = ' ' + (col.currencyUnit || col.defaultCurrency);
      } else if (col.isWeight && col.weightUnit) {
        suffix = ' ' + col.weightUnit;
      } else if (col.isLength && col.lengthUnit) {
        suffix = ' ' + col.lengthUnit;
      }
      if (suffix) return String(val) + suffix;
    }
    return val != null && val !== '' ? String(val) : '-';
  };

  const defaultRenderActions = (item) => {
    const viewRoute = routes?.view?.(item.entity_id) || `/${entityType}/view/${item.entity_id}`;
    const editRoute = routes?.edit?.(item.entity_id) || `/${entityType}/edit/${item.entity_id}`;
    return (
      <>
        {canView && (
          <button onClick={() => navigate(viewRoute)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title={t('common.view')}><Eye size={16} /></button>
        )}
        {canEdit && (
          <button onClick={() => navigate(editRoute, { state: { fromPage: page, fromPageSize: pageSize } })}
            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t('common.edit')}><Edit3 size={16} /></button>
        )}
        {canDelete && (
          <button onClick={() => handleDelete(item.entity_id)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.delete')}><Trash2 size={16} /></button>
        )}
      </>
    );
  };

  const cellRenderer = (item, col) => {
    if (renderCell) {
      const custom = renderCell(item, col);
      if (custom !== null && custom !== undefined) return custom;
    }
    return defaultRenderCell(item, col);
  };
  const actionsRenderer = renderActions
    ? (item) => renderActions(item, {
        handleDelete,
        loadItems: (p, s) => loadItems(p || page, s || pageSize),
        canView,
        canEdit,
        canDelete,
        page,
        pageSize,
      })
    : defaultRenderActions;

  // ================================================================
  // TREE RENDERING (for products with variants)
  // ================================================================
  const toggleExpand = (parentId) => {
    setExpandedParentIds(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const hasChildren = (parentId) =>
    treeField && items.some(item => item[treeField] === parentId);

  const getChildren = (parentId) =>
    treeField ? items.filter(item => item[treeField] === parentId) : [];

  const renderRows = (parentId = null, level = 0) => {
    // Root level: show only parents (item_group_id IS NULL)
    // Children: show only if parent is expanded and items match
    const filtered = treeField
      ? items.filter(item => item[treeField] === parentId)
      : items;

    return filtered.flatMap(item => {
      const nodeId = item[treeNodeIdField];
      const isExpandable = treeField && hasChildren(nodeId);
      const isExpanded = expandedParentIds.has(nodeId);
      const children = isExpanded ? renderRows(nodeId, level + 1) : [];

      const row = (
        <tr key={nodeId}
          className={`hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${level > 0 ? 'bg-slate-50/50 dark:bg-slate-700/50' : ''}`}>
          {showCheckboxCol && (
            <td className="px-3 py-3 border-b border-slate-100 dark:border-slate-800 text-center" style={{ width: 48, minWidth: 48 }}>
              <div className="flex items-center justify-center gap-0">
                {treeField && (
                  <button type="button" onClick={() => toggleExpand(nodeId)}
                    className={`p-1 rounded transition-colors flex-shrink-0 ${isExpandable ? 'hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer' : 'cursor-default opacity-20'}`}
                    disabled={!isExpandable}
                    title={isExpanded ? 'Collapse' : (isExpandable ? 'Expand' : '')}>
                    <ChevronRight size={14}
                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
                )}
                <input type="checkbox" checked={selectedIds.includes(item.entity_id)}
                  onChange={() => toggleSelect(item.entity_id)}
                  className="flex-shrink-0 ml-1" />
              </div>
            </td>
          )}
          {visibleColumns.map(code => {
            const col = allColumns.find(c => c.code === code);
            if (!col) return null;
            const w = colWidths[code] || 100;
            const indent = treeField && code === 'sku' ? { paddingLeft: `${level * 20 + 12}px` } : {};
            return (
              <td key={code}
                className="px-3 py-3 border-b border-slate-100 dark:border-slate-800 text-sm text-center truncate"
                style={{ width: w, minWidth: w, maxWidth: w, ...indent }}>
                {cellRenderer(item, col)}
              </td>
            );
          })}
          {showActionsCol && (
            <td className="px-3 py-3 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap text-center" style={{ width: 120, minWidth: 120 }}>
              <div className="flex gap-1 items-center justify-center">
              {actionsRenderer(item)}
            </div>
          </td>
          )}
        </tr>
      );

      return [row, ...children];
    });
  };

  // ================================================================
  // RENDER
  // ================================================================
  if (loading && items.length === 0) return <div className="text-center p-10">{t('common.loading')}</div>;

  const showCheckboxCol = canDelete;
  const showActionsCol = canView || canEdit || canDelete;
  const totalCols = visibleColumns.length + (showCheckboxCol ? 1 : 0) + (showActionsCol ? 1 : 0);

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">{t(titleKey)}</h1></div>

      <div className="flex items-center gap-2 flex-wrap">
          {canAdd && (
            <button onClick={() => navigate(routes?.add || `/${entityType}/new`)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors">
              <Plus size={18} />{t('common.add')}
            </button>
          )}
          {headerActionsExtra}
        </div>

      {/* Toolbar */}
      <div className="relative">
        <div className="flex flex-col md:flex-row flex-wrap gap-3 md:items-end">
          <div className="relative w-full md:flex-1 md:min-w-[200px] md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') loadItems(1); }}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              placeholder={t(searchKey)} />
          </div>
          {typeof filterBarExtra === 'function' ? filterBarExtra(filters, setFilters) : filterBarExtra}

          <div className="relative w-full md:w-auto">
            <button onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              className="w-full md:w-auto flex items-center justify-center md:justify-start gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white">
              <Filter size={16} />{t('common.filters')}
              {isFiltersExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {isFiltersExpanded && (
              <div className="absolute left-0 md:left-auto right-0 md:right-auto top-full mt-1 w-full md:min-w-[600px] lg:min-w-[800px] xl:min-w-[1000px] bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 z-[9999] animate-in slide-in-from-top-2 duration-200 shadow-xl">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-h-[400px] overflow-y-auto">
                  {allColumns.filter(col => !excludeFilterColumns.includes(col.code)).map(col => {
                    const customRenderer = filterCustomRenderers[col.code];
                    return (
                      <div key={col.code} className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{col.label}</label>
                        {customRenderer
                          ? customRenderer(col, filters[col.code] || '', (v) => setFilters({...filters, [col.code]: v}), filters, setFilters)
                          : renderFilterInput(col)
                        }
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setFilters({})}
                    className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                    {t('common.reset_all')}
                  </button>
                  <button onClick={() => loadItems(1)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-md transition-all">
                    {t('common.apply_filters')}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="w-full md:w-auto flex flex-col md:flex-row flex-wrap gap-3">
            <div className="w-full md:w-auto">
              <EavView currentView={currentView} views={savedViews}
                onSelectView={(view) => {
                  setCurrentView(view.name);
                  if (view.columns && view.columns.length > 0) {
                    setVisibleColumns(view.columns);
                  } else {
                    // 默认视图：重置为 defaultVisible
                    setVisibleColumns(defaultVisible);
                  }
                }}
                onSaveView={(name) => {
                  const newView = { name, columns: visibleColumns };
                  const newViews = [...savedViews, newView];
                  setSavedViews(newViews);
                  try { localStorage.setItem(`eav_views_${entityType}`, JSON.stringify(newViews)); } catch {}
                  setCurrentView(name);
                }}
                onDeleteView={(view) => {
                  const newViews = savedViews.filter(v => v.name !== view.name);
                  setSavedViews(newViews);
                  try { localStorage.setItem(`eav_views_${entityType}`, JSON.stringify(newViews)); } catch {}
                  if (currentView === view.name && newViews.length > 0) {
                    setCurrentView(newViews[0].name);
                    if (newViews[0].columns && newViews[0].columns.length > 0) {
                      setVisibleColumns(newViews[0].columns);
                    }
                  } else if (newViews.length === 0) {
                    setCurrentView(t('products.default_view'));
                  }
                }} />
            </div>
            <div className="w-full md:w-auto">
              <EavColumns allColumns={allColumns} visibleColumns={visibleColumns} setVisibleColumns={setVisibleColumns} />
            </div>
            {extraToolbarButtons}
            <div className="w-full md:w-auto">
              <EavAction selectedIds={selectedIds}
                onBatchDelete={handleBatchDelete}
                onBatchUpdate={canEdit ? handleBatchUpdate : undefined}
                onBatchAssignResponsible={onBatchAssign}
                onBatchRemoveResponsible={onBatchRemoveResponsible}
                onBatchQcUpdate={onBatchQcUpdate}
                onBatchPushToChannel={onBatchPushToChannel}
                onBatchRecalculateFormulas={onBatchRecalculateFormulas}
                onBatchEditStatus={onBatchEditStatus}
                onBatchGenerateUrlKey={onBatchGenerateUrlKey}
                onBatchUpdateSyncStatus={onBatchUpdateSyncStatus}
                onBatchUpdatePushStatus={onBatchUpdatePushStatus}
                onBatchUpdateOnline={onBatchUpdateOnline}
                onBatchUpdatePrice={onBatchUpdatePrice}
                onBatchUpdateImages={onBatchUpdateImages}
                onBatchAssignPosition={onBatchAssignPosition} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-300 text-xs font-semibold tracking-wider select-none">
              <tr>
                {showCheckboxCol && (
                  <th className="px-3 py-3 border-b border-slate-200 dark:border-slate-700 text-center"
                    style={{ width: 48, minWidth: 48 }}>
                    <div className="flex items-center justify-center gap-0">
                      <input type="checkbox" onChange={toggleSelectAll}
                        checked={selectedIds.length === items.length && items.length > 0}
                        className="flex-shrink-0 ml-1" />
                    </div>
                  </th>
                )}
                {visibleColumns.map((code, ci) => {
                  const col = allColumns.find(c => c.code === code);
                  const w = colWidths[code] || 100;
                  const isOver = dragOverIdx === ci;
                  return (
                    <th key={code}
                      draggable
                      onDragStart={e => handleHeaderDragStart(e, code, ci)}
                      onDragOver={e => handleHeaderDragOver(e, ci)}
                      onDrop={e => handleHeaderDrop(ci)}
                      onDragEnd={dragAfterDrop}
                      className="px-3 py-3 border-b border-slate-200 dark:border-slate-700 text-center relative group truncate"
                      style={{
                        width: w, minWidth: w, maxWidth: w,
                        borderLeft: isOver ? '2px solid #3b82f6' : undefined,
                        borderRight: isOver ? '2px solid #3b82f6' : undefined,
                        cursor: 'grab',
                      }}>
                      <div className="flex items-center justify-center gap-1">
                        <span
                          className={"truncate" + (code === 'entity_id' ? ' cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 select-none' : '')}
                          onClick={code === 'entity_id' ? () => {
                            if (sortField === 'entity_id') {
                              if (sortOrder === 'DESC') {
                                setSortField('entity_id');
                                setSortOrder('ASC');
                              } else {
                                setSortField('');
                                setSortOrder('DESC');
                              }
                            } else {
                              setSortField('entity_id');
                              setSortOrder('DESC');
                            }
                          } : undefined}
                        >{col ? col.label : code}</span>
                        {code === 'entity_id' && sortField === 'entity_id' && (
                          sortOrder === 'DESC' ? (
                            <ChevronDown size={14} className="text-blue-500 flex-shrink-0" />
                          ) : (
                            <ChevronUp size={14} className="text-blue-500 flex-shrink-0" />
                          )
                        )}
                      </div>
                      <div
                        onMouseDown={e => handleResizeStart(code, e)}
                        className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize hover:bg-blue-400/60 active:bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10" />
                    </th>
                  );
                })}
                {showActionsCol && (
                  <th className="px-3 py-3 border-b border-slate-200 dark:border-slate-700 text-center"
                    style={{ width: 120, minWidth: 120 }}>
                    {t(actionsLabelKey)}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {treeField ? (items.filter(item => item[treeField] === null).length > 0 ? renderRows() : (
                <tr><td colSpan={totalCols} className="px-6 py-10 text-center text-slate-400 italic">{t('common.no_data')}</td></tr>
              )) : (items.length > 0 ? renderRows() : (
                <tr><td colSpan={totalCols} className="px-6 py-10 text-center text-slate-400 italic">{t('common.no_data')}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {usePageSizeChange ? (
          <Pagination
            currentPage={page}
            totalItems={total}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            onPageChange={(p, size) => loadItems(p, size || pageSize)}
            extraInfo={skusTotal > 0 ? (
              <span className="text-slate-400 dark:text-slate-500">
                {t('products.product_count')}: {productsTotal}, {t('products.sku_count')}: {skusTotal}
              </span>
            ) : null}
          />
        ) : (
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
            <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              <span>{t('products.page_total')} {total} {t('common.items')}</span>
              {skusTotal > 0 && (
                <span className="text-slate-400 dark:text-slate-500">
                  ({t('products.product_count')}: {productsTotal}, {t('products.sku_count')}: {skusTotal})
                </span>
              )}
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span>{t('components.pagination.items_per_page')}:</span>
              <select value={pageSize} onChange={e => {
                const sz = parseInt(e.target.value, 10);
                if (sz > 0) { setPageSize(sz); loadItems(1, sz); }
              }}
                className="p-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 dark:text-white text-sm outline-none">
                {[10, 20, 30, 40, 50].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button disabled={page === 1} onClick={() => loadItems(page - 1)}
                className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 disabled:opacity-50 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                {t('products.prev_page')}
              </button>
              <span className="text-sm text-slate-500 dark:text-slate-400 min-w-[3rem] text-center">{page} / {Math.ceil(total / pageSize) || 1}</span>
              <button disabled={page * pageSize >= total} onClick={() => loadItems(page + 1)}
                className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 disabled:opacity-50 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                {t('products.next_page')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EavList;
